import React, { useEffect, useState, useMemo } from 'react';
import { Trash2, Loader2, X, Check, RefreshCw, ArrowRight, Star, Pencil, ChevronDown, ChevronUp, Calendar as CalendarIcon, List as ListIcon, ChevronLeft, ChevronRight, Search, Download, Filter } from 'lucide-react';
import { Transaction, ExpenseCategory } from '../types';
import { storageService } from '../services/storageService';
import { CATEGORY_COLORS, PAYMENT_METHOD_LABELS, POINT_PROVIDER_LABELS } from '../constants';

interface ExpenseListProps {
  userId: string;
  onEdit: (transaction: Transaction) => void;
}

type ViewMode = 'list' | 'calendar';

export const ExpenseList: React.FC<ExpenseListProps> = ({ userId, onEdit }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // New Features State
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDay, setSelectedDay] = useState<number | null>(null); // For calendar filtering

  useEffect(() => {
    loadTransactions();
  }, [userId]);

  const loadTransactions = async () => {
    setLoading(true);
    const data = await storageService.getTransactions(userId);
    setTransactions(data);
    setLoading(false);
  };

  const initiateDelete = (id: string) => {
    setDeletingId(id);
  };

  const cancelDelete = () => {
    setDeletingId(null);
  };

  const confirmDelete = async (id: string) => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await storageService.deleteTransaction(userId, id);
      await loadTransactions();
    } catch (error) {
      console.error("Failed to delete", error);
      alert("削除できませんでした。");
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
    }
  };

  const toggleExpand = (id: string) => {
      setExpandedId(expandedId === id ? null : id);
  };

  // --- CSV Export ---
  const handleExportCSV = () => {
      const headers = ['日付', '時刻', '種類', 'カテゴリ', '金額', '店名・内容', '支払方法', '詳細', 'メモ'];
      const csvContent = [
          headers.join(','),
          ...filteredTransactions.map(t => {
              const dt = new Date(t.date);
              const date = dt.toLocaleDateString('ja-JP');
              const time = dt.toLocaleTimeString('ja-JP');
              const clean = (s?: string) => `"${(s || '').replace(/"/g, '""')}"`;
              return [
                  date,
                  time,
                  t.type,
                  t.category,
                  t.amount,
                  clean(t.place),
                  t.method ? PAYMENT_METHOD_LABELS[t.method] : '',
                  clean(t.paymentDetail),
                  clean(t.note)
              ].join(',');
          })
      ].join('\n');

      const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `smart_ledger_${currentDate.getFullYear()}_${currentDate.getMonth() + 1}.csv`;
      link.click();
  };

  // --- Filtering Logic ---
  const filteredTransactions = useMemo(() => {
      return transactions.filter(t => {
          const tDate = new Date(t.date);
          const sameMonth = tDate.getMonth() === currentDate.getMonth() && tDate.getFullYear() === currentDate.getFullYear();
          if (!sameMonth) return false;

          // Calendar Selection Filter
          if (viewMode === 'calendar' && selectedDay !== null) {
              if (tDate.getDate() !== selectedDay) return false;
          }

          // Search Filter
          if (searchQuery) {
              const q = searchQuery.toLowerCase();
              return (
                  t.place.toLowerCase().includes(q) ||
                  t.note?.toLowerCase().includes(q) ||
                  t.items?.some(i => i.name.toLowerCase().includes(q)) ||
                  t.category.includes(q)
              );
          }

          return true;
      });
  }, [transactions, currentDate, searchQuery, viewMode, selectedDay]);

  // Group by Date for List View
  const groupedTransactions = useMemo(() => {
    return filteredTransactions.reduce((groups, transaction) => {
        const dateKey = transaction.date.includes('T') ? transaction.date.split('T')[0] : transaction.date;
        if (!groups[dateKey]) {
        groups[dateKey] = [];
        }
        groups[dateKey].push(transaction);
        return groups;
    }, {} as Record<string, Transaction[]>);
  }, [filteredTransactions]);

  // --- Month Navigation ---
  const prevMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
      setSelectedDay(null);
  };
  const nextMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
      setSelectedDay(null);
  };

  // --- Calendar Logic ---
  const renderCalendar = () => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      const days = [];
      // Empty cells
      for (let i = 0; i < firstDay; i++) {
          days.push(<div key={`empty-${i}`} className="h-20 sm:h-24"></div>);
      }
      
      // Days
      for (let d = 1; d <= daysInMonth; d++) {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          
          // Calculate daily total for this day
          const dayTransactions = transactions.filter(t => t.date.startsWith(dateStr) && t.type !== 'income');
          const dayIncome = transactions.filter(t => t.date.startsWith(dateStr) && t.type === 'income');
          const totalAmount = dayTransactions.reduce((sum, t) => sum + t.amount, 0);
          const hasIncome = dayIncome.length > 0;
          
          const isSelected = selectedDay === d;

          days.push(
              <button 
                key={d} 
                onClick={() => setSelectedDay(isSelected ? null : d)}
                className={`h-20 sm:h-24 relative p-1 flex flex-col items-center justify-start group`}
                style={{
                  borderTop:'1px solid rgba(44,36,22,0.06)',
                  borderRight:'1px solid rgba(44,36,22,0.06)',
                  background: isSelected ? 'var(--forest-pale)' : 'white',
                  transition: 'background 0.2s ease',
                  animation: `fadeIn 0.3s ease-out ${d * 0.012}s both`,
                }}
              >
                  <span
                    className="text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full mb-1 transition-all duration-200 group-hover:scale-110"
                    style={isSelected
                      ? {background:'var(--forest)', color:'white', boxShadow:'0 2px 8px rgba(45,90,61,0.3)'}
                      : {color:'var(--ink-light)'}}
                  >
                      {d}
                  </span>
                  
                  {totalAmount > 0 && (
                      <span
                        className="text-[10px] font-bold break-all leading-tight transition-transform duration-200 group-hover:scale-105"
                        style={{color:'var(--terra)'}}>
                          ¥{totalAmount.toLocaleString()}
                      </span>
                  )}
                  {hasIncome && (
                      <div className="w-1.5 h-1.5 rounded-full mt-1 animate-pulse-soft" style={{background:'var(--forest)'}} />
                  )}
                  {/* hover shimmer overlay */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                    style={{background:'rgba(45,90,61,0.04)'}} />
              </button>
          );
      }

      return days;
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-2">
        <Loader2 className="w-7 h-7 animate-spin" style={{color:'var(--forest)'}} />
        <span className="text-xs" style={{color:'var(--ink-muted)'}}>読み込み中...</span>
      </div>
    );
  }

  const isFilterActive = searchQuery !== '' || selectedDay !== null;

  const TYPE_TAG: Record<string, {label:string, bg:string, color:string}> = {
    income:     {label:'収入',    bg:'var(--forest-pale)', color:'var(--forest)'},
    charge:     {label:'チャージ', bg:'#EAF4FA',            color:'#1a7a9e'},
    point:      {label:'ポイント', bg:'var(--gold-pale)',   color:'#8B6914'},
    adjustment: {label:'修正',    bg:'var(--cream-dark)',  color:'var(--ink-muted)'},
    expense:    {label:'支出',    bg:'var(--terra-pale)',  color:'var(--terra)'},
  };

  return (
    <div className="space-y-3 min-h-[80vh] animate-fade-up">

      {/* ── Controls ── */}
      <div className="sticky top-0 z-20 pb-2 space-y-2.5" style={{background:'var(--cream)'}}>
        <div className="flex items-center justify-between animate-fade-down">
          {/* Month navigator */}
          <div className="flex items-center gap-1 card px-1 py-1 hover-lift">
            <button onClick={prevMonth}
              className="p-1.5 rounded-lg transition-all duration-200 hover:scale-110 active:scale-90"
              style={{color:'var(--ink-muted)'}}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold min-w-[96px] text-center font-display" style={{color:'var(--ink)'}}>
              {currentDate.getFullYear()}年 {currentDate.getMonth()+1}月
            </span>
            <button onClick={nextMonth}
              className="p-1.5 rounded-lg transition-all duration-200 hover:scale-110 active:scale-90"
              style={{color:'var(--ink-muted)'}}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* CSV export */}
            <button onClick={handleExportCSV}
              className="card p-2.5 transition-all duration-200 hover:scale-110 active:scale-90 hover-lift"
              style={{color:'var(--ink-muted)'}} title="CSV出力">
              <Download className="w-4 h-4" />
            </button>

            {/* View toggle */}
            <div className="card p-1 flex gap-0.5">
              <button onClick={() => { setViewMode('list'); setSelectedDay(null); }}
                className="p-2 rounded-lg ripple-container"
                style={{
                  background: viewMode==='list' ? 'var(--forest)' : 'transparent',
                  color: viewMode==='list' ? 'white' : 'var(--ink-muted)',
                  transform: viewMode==='list' ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)'
                }}>
                <ListIcon className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('calendar')}
                className="p-2 rounded-lg ripple-container"
                style={{
                  background: viewMode==='calendar' ? 'var(--forest)' : 'transparent',
                  color: viewMode==='calendar' ? 'white' : 'var(--ink-muted)',
                  transform: viewMode==='calendar' ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)'
                }}>
                <CalendarIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative animate-slide-down delay-50">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-200"
            style={{color: searchQuery ? 'var(--forest)' : 'var(--ink-muted)'}} />
          <input
            type="text"
            placeholder="店舗・メモ・カテゴリで検索..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="input-warm w-full pl-9 pr-9 py-2.5 text-sm"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 animate-scale-bounce transition-transform hover:scale-110 active:scale-90"
              style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}>
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {selectedDay && viewMode === 'calendar' && (
          <div className="flex items-center justify-between px-3 py-2 rounded-xl text-xs animate-slide-down"
            style={{background:'var(--forest-pale)', color:'var(--forest)'}}>
            <span className="font-semibold">{currentDate.getMonth()+1}月{selectedDay}日の明細</span>
            <button onClick={() => setSelectedDay(null)} className="font-semibold underline">すべて表示</button>
          </div>
        )}
      </div>

      {/* ── Calendar View ── */}
      {viewMode === 'calendar' && (
        <div className="card overflow-hidden animate-scale-in">
          <div className="grid grid-cols-7" style={{background:'var(--cream-dark)'}}>
            {['日','月','火','水','木','金','土'].map((d,i) => (
              <div key={d} className="text-center py-2 text-[10px] font-bold"
                style={{color: i===0?'var(--terra)': i===6?'#1a5a9e':'var(--ink-muted)'}}>
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7" style={{borderTop:'1px solid rgba(44,36,22,0.06)'}}>
            {renderCalendar()}
          </div>
        </div>
      )}

      {/* ── List View ── */}
      {(viewMode === 'list' || (viewMode === 'calendar' && selectedDay !== null)) && (
        <div className="space-y-5">
          {Object.keys(groupedTransactions).length === 0 ? (
            <div className="text-center py-16" style={{color:'var(--ink-muted)'}}>
              <Filter className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">該当する記録がありません</p>
            </div>
          ) : (
            Object.entries(groupedTransactions).sort((a,b) => b[0].localeCompare(a[0])).map(([date, items]: [string, Transaction[]], groupIndex) => {
              const d = new Date(parseInt(date.split('-')[0]), parseInt(date.split('-')[1])-1, parseInt(date.split('-')[2]));
              const dayLabel = d.toLocaleDateString('ja-JP', {weekday:'short'});
              const dayNum = parseInt(date.split('-')[2]);
              const dailyExpense = items.filter(i => i.type==='expense').reduce((s,i)=>s+i.amount,0);
              const dailyIncome = items.filter(i => i.type==='income').reduce((s,i)=>s+i.amount,0);

              return (
                <div key={date} className="stagger-child animate-fade-up" style={{animationDelay:`${groupIndex*0.06}s`}}>
                  {/* Date header */}
                  <div className="flex items-center gap-3 mb-2 px-1">
                    <div className="flex flex-col items-center w-8">
                      <span className="font-display text-xl font-600" style={{color:'var(--ink)', lineHeight:1}}>{dayNum}</span>
                      <span className="text-[10px] font-semibold" style={{color:'var(--ink-muted)'}}>{dayLabel}</span>
                    </div>
                    <div className="flex-1 dash-divider" />
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      {dailyIncome > 0 && (
                        <span className="animate-slide-left" style={{color:'var(--forest)'}}>+¥{dailyIncome.toLocaleString()}</span>
                      )}
                      {dailyExpense > 0 && (
                        <span className="animate-slide-left delay-50" style={{color:'var(--terra)'}}>-¥{dailyExpense.toLocaleString()}</span>
                      )}
                    </div>
                  </div>

                  {/* Transaction rows */}
                  <div className="card overflow-hidden">
                    {items.map((item, itemIndex) => {
                      const isIncome = item.type==='income';
                      const isCharge = item.type==='charge';
                      const isPoint = item.type==='point';
                      const isAdj = item.type==='adjustment';
                      const hasItems = item.items && item.items.length > 0;
                      const isExpanded = expandedId === item.id;
                      const typeTag = TYPE_TAG[item.type] || TYPE_TAG.expense;
                      const timeStr = item.date.includes('T') ? item.date.split('T')[1].slice(0,5) : null;
                      const dotColor = isIncome?'var(--forest)': isCharge?'#1a7a9e': isPoint?'var(--gold)': isAdj?'var(--ink-muted)':'var(--terra)';
                      const amountColor = isIncome?'var(--forest)': isCharge?'#1a7a9e': isPoint?'var(--gold)': isAdj?'var(--ink-muted)':'var(--terra)';

                      if (deletingId === item.id) {
                        return (
                          <div key={item.id} className="px-4 py-3 flex items-center justify-between animate-bounce-in"
                            style={{background:'var(--terra-pale)'}}>
                            <div>
                              <span className="text-sm font-semibold" style={{color:'var(--terra)'}}>削除しますか？</span>
                              <p className="text-xs mt-0.5" style={{color:'var(--terra-light)'}}>{item.place} ¥{item.amount.toLocaleString()}</p>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={cancelDelete}
                                className="p-2 rounded-lg card transition-transform active:scale-90"
                                style={{color:'var(--ink-muted)'}}>
                                <X className="w-4 h-4" />
                              </button>
                              <button onClick={() => confirmDelete(item.id)} disabled={isDeleting}
                                className="btn-terra p-2 rounded-lg flex items-center transition-transform active:scale-90">
                                {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={item.id}
                          className={`ledger-row ${itemIndex===0?'':'border-t'}`}
                          style={{
                            borderColor:'rgba(44,36,22,0.06)',
                            animationDelay:`${groupIndex*0.06 + itemIndex*0.04}s`
                          }}>
                          <div
                            className="px-4 py-3 flex items-center justify-between group transition-all duration-200 active:scale-[0.99]"
                            style={{cursor: hasItems ? 'pointer' : 'default'}}
                            onClick={() => hasItems && toggleExpand(item.id)}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              {/* Animated color dot */}
                              <div
                                className="w-2.5 h-2.5 rounded-full shrink-0 transition-transform duration-200 group-hover:scale-150"
                                style={{background: dotColor}} />
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-sm font-semibold truncate" style={{color:'var(--ink)', maxWidth:'140px'}}>
                                    {isCharge ? (
                                      <span className="flex items-center gap-1 text-xs">
                                        {item.sourceMethod && <span style={{color:'var(--ink-light)'}}>{PAYMENT_METHOD_LABELS[item.sourceMethod as keyof typeof PAYMENT_METHOD_LABELS]}</span>}
                                        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" style={{color:'var(--ink-muted)'}} />
                                        <span style={{color:'var(--ink)'}}>{PAYMENT_METHOD_LABELS[item.method]}</span>
                                      </span>
                                    ) : isPoint ? (
                                      item.pointProvider ? POINT_PROVIDER_LABELS[item.pointProvider] : 'ポイント'
                                    ) : item.place}
                                  </span>
                                  <span className="tag shrink-0 transition-all duration-200 group-hover:scale-105"
                                    style={{background:typeTag.bg, color:typeTag.color}}>
                                    {typeTag.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {timeStr && <span className="font-mono text-[10px]" style={{color:'var(--ink-muted)'}}>{timeStr}</span>}
                                  {!isCharge && !isPoint && (
                                    <span className="text-[10px]" style={{color:'var(--ink-muted)'}}>
                                      {PAYMENT_METHOD_LABELS[item.method as keyof typeof PAYMENT_METHOD_LABELS]}
                                      {item.paymentDetail && ` (${item.paymentDetail})`}
                                    </span>
                                  )}
                                  {item.note && <span className="text-[10px] truncate max-w-[80px]" style={{color:'var(--ink-muted)'}}>• {item.note}</span>}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              {/* Action buttons — slide in on hover */}
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0">
                                <button onClick={e => { e.stopPropagation(); onEdit(item); }}
                                  className="p-1.5 rounded-lg transition-transform active:scale-90 hover:scale-110"
                                  style={{background:'var(--cream-dark)', color:'var(--ink-muted)'}}>
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button onClick={e => { e.stopPropagation(); initiateDelete(item.id); }}
                                  className="p-1.5 rounded-lg transition-transform active:scale-90 hover:scale-110"
                                  style={{background:'var(--terra-pale)', color:'var(--terra)'}}>
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                              <span
                                className="font-mono text-sm font-600 ml-1 tabular-nums"
                                style={{color: amountColor, transition:'color 0.2s'}}>
                                {isIncome?'+':''}¥{item.amount.toLocaleString()}{isPoint?' P':''}
                              </span>
                              {hasItems && (
                                <span className="transition-transform duration-300" style={{color:'var(--ink-muted)', transform: isExpanded?'rotate(180deg)':'rotate(0deg)'}}>
                                  <ChevronDown className="w-3 h-3" />
                                </span>
                              )}
                            </div>
                          </div>

                          {hasItems && isExpanded && (
                            <div className="px-4 pb-3 animate-slide-down" style={{borderTop:'1px dashed rgba(44,36,22,0.08)'}}>
                              <ul className="space-y-1.5 pt-2.5">
                                {item.items?.map((prod, idx) => (
                                  <li key={idx}
                                    className="flex justify-between text-xs stagger-child animate-slide-right"
                                    style={{animationDelay:`${idx*0.05}s`}}>
                                    <span style={{color:'var(--ink-light)'}}>{prod.name}</span>
                                    <span className="font-mono" style={{color:'var(--ink-muted)'}}>
                                      {prod.amount !== undefined ? `¥${prod.amount.toLocaleString()}` : '—'}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      <div className="h-8" />
    </div>
  );
};
